import { ICitationOptions } from '@readcube/smartcite-shared';
import { CitationMeta } from '../models/models';

const itemAsAuthorYearPair = (item: any) => {

  let firstAuthorSurname =
    item.article.authors?.[0]?.split(' ')?.pop() || 'No author';

  if (firstAuthorSurname?.length && firstAuthorSurname[firstAuthorSurname.length - 1] === '~')
    firstAuthorSurname = firstAuthorSurname.slice(0, -1);

  const year =
    item.article.year || getYearFromMetadata(item.custom_metadata?.date) || 'No year';

  return `${firstAuthorSurname} ${year}`;
};

const getYearFromMetadata = (val) => {
  if (!val)
    return;

  if (/^\d{1,4}$/.test(val) && val > 0)
    return val;

  let dateParts = /^(\d{1,4})-(\d{1,2})$/.exec(val);

  const hasYearMonth =
    dateParts
    && dateParts[1] && (parseInt(dateParts[1]) > 0)
    && dateParts[2] && (parseInt(dateParts[2]) > 0);

  if (hasYearMonth)
    return dateParts[1];

  dateParts = /^(\d{1,4})-(\d{1,2})-(\d{1,2})$/.exec(val);

  const hasYearMonthDate =
    dateParts
    && dateParts[1] && (parseInt(dateParts[1]) > 0)
    && dateParts[2] && (parseInt(dateParts[2]) > 0)
    && dateParts[3] && (parseInt(dateParts[3]) > 0);

  if (hasYearMonthDate)
    return dateParts[1];
};

export const itemsAsDefaultCitationText = (items: any) => {

  const itemsText =
    items
      .map(itemAsAuthorYearPair)
      .join(',');

  return `(${itemsText})`;
};

export const itemsAsRefs = (items: any[]) =>
  items
    .map(i => i.collection_id + ':' + i.id);

const itemAsRef = (item: any) => item.collection_id + ':' + item.id;

// The external-id kinds recovery can actually use, mirroring shared's `IExtIds` — which is also
// exactly what the `status/check_ext_ids` endpoint matches on.
const EXT_ID_KEYS = ['doi', 'pmid', 'pmcid', 'gsid', 'arxiv'];

/**
 * Reduces an item's `ext_ids` to the keys recovery can use that actually have a value, or null when
 * nothing is left.
 *
 * Worth being strict here, because whatever survives is percent-encoded into the citation hyperlink
 * of *every* citation of that item, in every user document, forever. The API returns the kinds it
 * has no value for as explicit nulls, and shared's `mapItemData` passes the object through verbatim
 * (`ext_ids: item.ext_ids || {}`), so the raw object costs ~80 wasted characters per ref
 * (`%22pmid%22%3Anull` and friends). Whitelisting also stops a new API field from silently
 * inflating every link in every document the next time the sync payload grows.
 */
const pruneExtIds = (extIds: any): { [key: string]: string; } | null => {
  if (!extIds)
    return null;

  const pruned: { [key: string]: string; } = {};

  EXT_ID_KEYS.forEach(key => {
    if (extIds[key])
      pruned[key] = extIds[key];
  });

  return Object.keys(pruned).length ? pruned : null;
};

// Builds the ref -> ext_ids map for a citation's items, so external ids can be stored alongside
// the refs in the citation hyperlink. Only items that actually carry usable external ids are
// included — an entry with nothing in it would just bloat every citation URL.
export const itemsAsExtIds = (items: any[]): { [ref: string]: any; } =>
  (items || []).reduce((map, item) => {
    const extIds = item && pruneExtIds(item.ext_ids);

    if (extIds)
      map[itemAsRef(item)] = extIds;

    return map;
  }, {});

/**
 * Given the items resolved for a reformat, produce the meta to persist for one citation.
 *
 * Recovered items carry `originalRef` — the now-dead `collection_id:item_id` they stand in for — so
 * this re-points those refs to the recovered item's new id and refreshes the stored ext_ids.
 *
 * Two subtleties worth keeping:
 *  - A ref that resolved to nothing but still has ext_ids is KEPT, so it can be recovered on a
 *    later pass. A ref with neither an item nor ext_ids is DROPPED — otherwise a dead,
 *    unrecoverable ref lingers forever and re-triggers the missing-references prompt on every
 *    single bibliography update.
 *  - `options.items` is keyed by ref, so re-pointing a ref without re-keying its options would
 *    silently discard that item's prefix/suffix/locator. (sc-word's port does not do this; its
 *    `remapAndBackfillMeta` passes `options` through untouched.)
 */
export const remapAndBackfillMeta = (meta: CitationMeta, items: any[]): CitationMeta => {
  const resolved = items || [];
  const newExtIds: { [ref: string]: any; } = {};
  const refRemap: { [oldRef: string]: string; } = {};

  const refs = (meta.refs || []).reduce((kept: string[], ref) => {
    const recovered = resolved.find(item => item && item.originalRef === ref);
    const item = recovered || resolved.find(item => item && itemAsRef(item) === ref);
    const effectiveRef = recovered ? itemAsRef(recovered) : ref;

    // Prefer fresh ext_ids off the resolved item; fall back to what the link already stored, so an
    // unresolved-but-recoverable ref keeps its recovery information. Both sides are pruned, so a
    // link written before pruning existed gets cleaned up on its next bibliography update.
    const extIds =
      pruneExtIds(item?.ext_ids)
      || pruneExtIds(meta.extIds?.[ref]);

    if (!item && !extIds)
      return kept;

    if (extIds)
      newExtIds[effectiveRef] = extIds;

    if (effectiveRef !== ref)
      refRemap[ref] = effectiveRef;

    kept.push(effectiveRef);
    return kept;
  }, []);

  const remapped: CitationMeta = {
    refs,
    options: remapOptionItemKeys(meta.options, refRemap)
  };

  if (Object.keys(newExtIds).length)
    remapped.extIds = newExtIds;

  return remapped;
};

// Re-keys `options.items` when refs were re-pointed by recovery, and drops per-item options for
// refs that are no longer part of the citation.
const remapOptionItemKeys = (
  options: ICitationOptions | undefined,
  refRemap: { [oldRef: string]: string; }
): ICitationOptions | undefined => {
  if (!options?.items)
    return options;

  if (!Object.keys(refRemap).length)
    return options;

  const items: { [ref: string]: any; } = {};

  Object.keys(options.items).forEach(ref => {
    items[refRemap[ref] || ref] = options.items[ref];
  });

  return { ...options, items };
};
